import { Button } from '@zendeskgarden/react-buttons'
import { Item as Option } from '@zendeskgarden/react-dropdowns'
import { Body, Close, Footer, FooterItem, Header, Modal } from '@zendeskgarden/react-modals'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'

import { BaseRecord } from '../../models/BaseRecord'
import { Category } from '../../models/Category'
import { Contract } from '../../models/Contract'
import { Item } from '../../models/Item'
import { PSRecord } from '../../models/PSRecord'
import { RecordFactory } from '../../models/RecordFactory'
import { Signature } from '../../models/Signature'
import { Type as Form } from '../../models/Type'
import { User } from '../../models/User'
import { downloadURL, formURL, payURL, widgetURL } from '../../utils/urls'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import { PSDropdown } from '../app/PSDropdown'

export type Link = {
    id: string
    name: string
    message: string
}

export type AttachmentType = 'link' | 'contract' | 'form'
export type Attachment = Link | Contract | Form

type Props = {
    attachmentType: AttachmentType
    currentThread: BaseRecord
    onClose: () => void
    onSend: (attachment: Attachment) => void
}

export const MessageAttachmentModal: React.FC<Props> = ({
    attachmentType,
    currentThread,
    onClose,
    onSend,
}) => {
    const owner = PSApplication.owner()

    const [isLoading, setIsLoading] = useState(false)
    const [sendEnabled, setSendEnabled] = useState(false)
    const [selectedOption, setSelectedOption] = useState<Attachment>()
    const [options, setOptions] = useState<Attachment[]>([])

    const getLinks = useCallback(
        (categories: Category[], items: Item[], forms: Form[]) => {
            if (!owner) return []

            const links: Link[] = []

            if (owner.allowLink) {
                links.push({
                    id: 'link',
                    name: 'Suite link',
                    message: `{firstname}, you can tap on my Suite link below: ${widgetURL({ user: owner })}`,
                })
            }

            if (owner.allowPaypal) {
                links.push({
                    id: 'pay',
                    name: 'Send payment',
                    message: `{firstname}, you can tap on the link below to easily pay me: ${payURL(owner)}`,
                })
            }

            links.push({
                id: 'app',
                name: 'Download PocketSuite',
                message: `{firstname}, you can download the PocketSuite app from here: ${downloadURL()}`,
            })

            if (owner.allowWidget) {
                links.push({
                    id: 'book',
                    name: 'Schedule me',
                    message: `{firstname}, you can tap on the link below to easily see my schedule and book me: ${widgetURL({ user: owner })}`,
                })

                if (owner.allowCategory) {
                    categories.forEach((category: Category) => {
                        links.push({
                            id: category.id || '',
                            name: category.name || '',
                            message: `{firstname}, you can tap on the link below to easily see my schedule and book me: ${widgetURL({ user: owner, category })}`,
                        })
                    })
                }

                items.forEach((item: Item) => {
                    if (item.endDate && moment(item.endDate).isBefore(moment())) return
                    if (
                        !item.type ||
                        ![
                            'service',
                            'class',
                            'reservation',
                            'package',
                            'gift',
                            'subscription',
                        ].includes(item.type)
                    )
                        return

                    links.push({
                        id: item.id || '',
                        name: item.name || '',
                        message: `{firstname}, you can tap on the link below to easily see my schedule and book me: ${widgetURL({ user: owner, item })}`,
                    })
                })
            }

            if (owner.allowRecord) {
                forms.forEach((form: Form) => {
                    if (!form.isPublic) return

                    links.push({
                        id: form.id || '',
                        name: form.name || '',
                        message: `{firstname}, you can tap on the link below to easily fill out my form: ${formURL(form)}`,
                    })
                })
            }

            return links
        },
        [owner]
    )

    useEffect(() => {
        if (isLoading) return

        const loadData = async () => {
            setIsLoading(true)

            if (attachmentType === 'link' && owner?.id) {
                const categories =
                    owner.allowWidget && owner.allowCategory
                        ? await RecordFactory.getInstance().loadCategories(owner.id)
                        : []
                const items = owner.allowWidget
                    ? await RecordFactory.getInstance().loadItems(owner.id)
                    : []
                const forms = owner.allowRecord
                    ? await RecordFactory.getInstance().loadTypes(owner.id)
                    : []
                const links = getLinks(categories, items, forms)
                setOptions(links)
            }
            else if (attachmentType === 'contract') {
                const contracts = owner.allowContract
                    ? await RecordFactory.getInstance().loadContracts()
                    : []
                setOptions(contracts)
            }
            else if (attachmentType === 'form' && owner?.id) {
                const forms = owner.allowRecord
                    ? await RecordFactory.getInstance().loadTypes(owner.id)
                    : []
                setOptions(forms)
            }

            setIsLoading(false)
        }

        loadData()
    }, [owner, attachmentType, getLinks])

    useEffect(() => {
        setSendEnabled(!!selectedOption)
    }, [selectedOption])

    const sendItem = async () => {
        if (!selectedOption) return

        if (attachmentType === 'contract') {
            const contract = selectedOption as Contract
            const signature = new Signature()
            signature.contract = contract
            signature.channel = 'sms'
            signature.person = currentThread as User

            await RecordFactory.getInstance().sendSignature(signature, false)
        }
        else if (attachmentType === 'form') {
            const form = selectedOption as Form
            const record = new PSRecord()
            record.type = form
            record.contract = form.contract
            record.channel = 'sms'
            record.person = currentThread as User
            record.name = form.name

            await RecordFactory.getInstance().sendRecord(record, false, false)
        }

        onSend(selectedOption)
    }

    return (
        <Modal className="attachmentModal" onClose={onClose}>
            <Header>Send client a {attachmentType}</Header>
            <Body>
                {isLoading && <Spinner />}
                {!isLoading && (
                    <PSDropdown
                        selected={selectedOption}
                        onSelect={setSelectedOption}
                        nameProperty="name"
                        label={`Select a ${attachmentType}`}
                        placeholder={<div className="grey">Select {attachmentType}</div>}
                    >
                        {options.map((option) => (
                            <Option key={`option-${option.id}`} value={option}>
                                {option.name}
                            </Option>
                        ))}
                    </PSDropdown>
                )}
            </Body>
            <Footer style={{ paddingBottom: 32 }}>
                <FooterItem>
                    <Button onClick={sendItem} disabled={!sendEnabled} isPrimary>
                        Send {attachmentType}
                    </Button>
                </FooterItem>
            </Footer>
            <Close aria-label="Close modal" />
        </Modal>
    )
}
